<template>
  <div class="opc-select-cycle">
    <!-- Text View Model -->
    <config-text-row
      v-if="textViewModel"
      :title="$t('billing.plan')"
      :value="billingCycleOptions?.[0]?.options?.[0]?.cycleLabel"
    />

    <!-- Cycle Select -->
    <div v-else class="opc-select-cycle--wrap">
      <span>{{ $t('billing.plan') }}</span>
      <cycle-select
        ref="opcSelectCycle"
        popper-append-to-body
        :model-value="billingCycle"
        @update:model-value="changeBillingCycle"
        :plans="currentGroupPlanList"
        :aria-label="`${planName} Plan`"
        :label="`${planName} Plan`"
        :post-label="getSavePercentLabel(billingCycle === BILLING_CYCLE.ANNUAL)"
        :action="switchBillingCycle"
        class="opc-capacity-config-row__capacity--select"
        floating-content-class="opc-capacity-config-row__capacity--popper"
        size="lg"
      >
        <template #trigger="{ label, value }">
          <span> {{ label }}</span>
          <save-count
            v-if='value === BILLING_CYCLE.ANNUAL'
            class="opc-select-cycle__inner"
            :annualPrice='currentPlanCyclePrice?.annual'
            :monthlyPrice='currentPlanCyclePrice?.monthly'
          />
        </template>
        <zm-select-option-group
          v-for="group in billingCycleOptions"
          :key="group.label"
          :label="group.label"
        >
          <zm-select-option
            v-for="p in group.options"
            :key="p.billing_cycle"
            :data-testid="`checkout-radio-billing-cycle-${p.plan_code}`"
            :disabled="p.disabled"
            :value="p.billing_cycle"
            :label="p.cycleLabel"
            :post-label="getSavePercentLabel(p.billing_cycle === BILLING_CYCLE.ANNUAL)"
          >
            <opc-option-item
              :annual-price="currentPlanCyclePrice?.annual"
              :monthly-price="currentPlanCyclePrice?.monthly"
              :is-show-save-count="p.billing_cycle === BILLING_CYCLE.ANNUAL"
              :label="p.cycleLabel"
              :tooltip-content="p.tooltips"
            />
          </zm-select-option>
        </zm-select-option-group>
      </cycle-select>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { planUnitMap } from '@/common/product-unit';
import { savePercent } from '@/components/save-percent.js';
import CycleSelect from '@/components/service/cycle-select.vue';
import OpcOptionItem from '@/components/zoom-ui-plus/select/option-item.vue';
import ConfigTextRow from '@/plans/EVENT_SAAS/component/config-text-row.vue';
import { BILLING_CYCLE } from '@/common/constants';
import { getBillingCycletranslationByKey, CycleSort } from '@/components/service/utils';
import { ZmSelectOption } from '@zoom/zoom-ui-vue3';
import saveCount from '@/components/save-count.vue';

export default {
  name: 'config-cycle-v2',
  components: { saveCount, ZmSelectOption, CycleSelect, ConfigTextRow, OpcOptionItem },
  emits: ['update:billingCycle', 'syncCycle', 'switchCycleAction'],
  props: {
    // billing cycle plan list
    billingCycleList: {
      type: Array,
      required: true,
      default: () => [],
    },
    billingCycle: String,
    unit: {
      type: Number,
      default: planUnitMap.license,
      validator: function(v) {
        return _.chain(planUnitMap)
          .values()
          .includes(v)
          .value();
      }
    },
    trackingPlanName: {
      type: String,
      required: false,
      default: '',
    },
    billingCycleGroupOptions: {
      type: Object,
      default: () => ({})
    },
    planName: {
      type: String,
      required: false,
      default: '',
    },
    textViewModel: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      BILLING_CYCLE,
      newPlan: {},
      oldPlan: {}
    }
  },
  computed: {
    currentPlanList() {
      return _.map(this.billingCycleList, (p) => p.plan);
    },
    currentGroupPlanList() {
      return this.billingCycleGroupOptions.map(plan => {
        return plan.options.map(p => p.plan);
      }).flat();
    },
    groupByPlanListByCycle() {
      return _.groupBy(this.currentPlanList, (p) => p?.billing_cycle);
    },
    cartSelectedPlan() {
      return _.find(this.currentPlanList, (p) => p?.cartInfo?.selected_number > 0);
    },
    cartSelectedPlanCode() {
      return this.cartSelectedPlan?.plan_code;
    },
    billingCycleOptions() {
      return this.billingCycleGroupOptions.map((group) => {
        return {
          ...group,
          options: group.options.reduce((prev,cur) => {
            const { plan, disabled, tooltips } = cur;
            prev.push({
              ...plan,
              disabled,
              tooltips,
              cycleLabel: getBillingCycletranslationByKey(plan.billing_cycle)
            });
            return prev;
          }, []).sort((a,b) => {
            if (CycleSort[a.billing_cycle] < CycleSort[b.billing_cycle]) {
              return 1;
            }
            if (CycleSort[a.billing_cycle] > CycleSort[b.billing_cycle]) {
              return -1;
            }
            return 0;
          })
        }
      })
    },
    currentPlanCyclePrice() {
      return this.currentPlanList.reduce((result, item) => {
        result[item.billing_cycle] = item.amount;
        return result;
      }, {});
    },
    getSavePercentLabel() {
      return (display = false) => {
        if (display) {
          const percent = savePercent({
            monthlyPrice: this.currentPlanCyclePrice?.monthly,
            annualPrice: this.currentPlanCyclePrice?.annual,
          });
          if (percent) {
            return 'Save ' + percent;
          }
          return null;
        }
        return null;
      }
    }
  },
  methods: {
    switchBillingCycle(oldPlan, newPlan) {
      this.oldPlan = oldPlan;
      this.newPlan = newPlan;
      this.$emit("switchCycleAction", oldPlan, newPlan);
    },
    changeBillingCycle(cycle) {
      this.$emit("update:billingCycle", cycle, this.oldPlan, this.newPlan);
      this.$emit("syncCycle", cycle, this.oldPlan, this.newPlan);
    },
    focus() {
      this.$refs.opcSelectCycle?.focus?.();
    }
  },
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';
.opc-capacity-config-row__capacity--popper {
  width: fit-content !important;
  .zbo-scrollbar__wrap {
    max-height: unset !important;
  }
}
.opc-select-cycle {
  color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
  font-size: 16px;
  margin-left: 16px;
  display: flex;

  :deep(.zbo-select) {
    border-radius: 10px;
    .zbo-select-input {
      padding-top: 4px;
      padding-bottom: 4px;
    }

    .zbo-select-input__wrapper {
      padding-left: unset;
      padding-top: unset;
      padding-bottom: unset;
      min-height: 28px;

      .zbo-select-input__span {
        color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
      }
    }
  }

  &__inner {
    margin-left: 8px;
  }

  &--wrap {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  @include media(mobile) {
    margin-left: 0;
    margin-top: 16px;
  }
  .zbo-select.zbo-select--xLarge {
    height: 48px;
    min-width: 200px;
  }
}
</style>
